import { defineStore } from "pinia";
import Api from "@/services/Api";
import CustomersServices from "@/services/Customer";
import ProductsServices from "@/services/Product";

export const useReservationStore = defineStore({
  id: "reservation",
  state: () => ({
    reservation_list: [], // This will now hold the full list from the backend for the current search/filters
    search_val: "", // Frontend-only search for displayed data
    status_filter: "", // Frontend-only filter for displayed data
    date_from: "", // Backend search parameter
    date_to: "", // Backend search parameter
    current_page: 1, // Pagination for frontend display
    items_per_page: 100, // Items per page for frontend display
    customer_list: [],
    product_list: [],
    product_search_term: "",
    customer_search_term: "",
  }),
  getters: {
    reservation_filtered() {
      let filtered = this.reservation_list.filter((document) => JSON.stringify(document).toLowerCase().includes(this.search_val.toLowerCase()));

      // Apply status filter
      if (this.status_filter) {
        filtered = filtered.filter((reservation) => {
          const status = this.getReservationStatus(reservation);
          return status === this.status_filter;
        });
      }

      // Apply date range filter
      if (this.date_from || this.date_to) {
        filtered = filtered.filter((reservation) => {
          const endDate = new Date(reservation.requested_for);
          const fromDate = this.date_from ? new Date(this.date_from) : null;
          const toDate = this.date_to ? new Date(this.date_to) : null;

          if (fromDate && endDate < fromDate) return false;
          if (toDate && endDate > toDate) return false;

          return true;
        });
      }

      return filtered.slice((this.current_page - 1) * this.items_per_page, this.current_page * this.items_per_page);
    },

    total_pages() {
      let filtered = this.reservation_list.filter((document) => JSON.stringify(document).toLowerCase().includes(this.search_val.toLowerCase()));

      // Apply status filter for pagination
      if (this.status_filter) {
        filtered = filtered.filter((reservation) => {
          const status = this.getReservationStatus(reservation);
          return status === this.status_filter;
        });
      }

      // Apply date range filter for pagination
      if (this.date_from || this.date_to) {
        filtered = filtered.filter((reservation) => {
          const endDate = new Date(reservation.requested_for);
          const fromDate = this.date_from ? new Date(this.date_from) : null;
          const toDate = this.date_to ? new Date(this.date_to) : null;

          if (fromDate && endDate < fromDate) return false;
          if (toDate && endDate > toDate) return false;

          return true;
        });
      }

      return Math.ceil(filtered.length / this.items_per_page);
    },

    // New getters for filtering products and customers
    filtered_products: (state) => {
      if (!state.product_search_term) return [];
      return state.product_list.filter((product) => product.name.toLowerCase().includes(state.product_search_term.toLowerCase()));
    },
    filtered_customers: (state) => {
      if (!state.customer_search_term) return [];
      return state.customer_list.filter((customer) => customer.name.toLowerCase().includes(state.customer_search_term.toLowerCase()));
    },
  },
  actions: {
    async get_reservations() {
      // Prepare request body parameters
      const data = {};
      if (this.date_from) {
        data.start_requested_for = this.date_from;
      }
      if (this.date_to) {
        data.end_requested_for = this.date_to;
      }
      // If you implement search_val to send to backend, add it here:
      // if (this.search_val) {
      //   data.search_val = this.search_val;
      // }

      await Api(true)
        .post("/reservation/get/data", data) // Use .post and pass data object as body
        .then((res) => {
          this.reservation_list = res.data;
          this.current_page = 1; // Reset to first page on new search
        })
        .catch((error) => {
          console.error("Error fetching reservations:", error);
          // Optionally, show a toast notification for the user
        });
    },
    async get_customers() {
      this.customer_list = await CustomersServices.getCustomers();
    },
    async get_products() {
      this.product_list = await ProductsServices.getProducts();
    },
    change_page(page) {
      this.current_page = page;
    },
    async handover(reservation) {
      var toaster = {};
      if (confirm("Jeni te sigurt qe doni dorezoni kete porosi?") == true) {
        await Api(true)
          .post("/reservation/handover", reservation)
          .then((res) => {
            if (res.data > 0) {
              this.reservation_list = this.reservation_list.map((a) => {
                if (a.guid == reservation.guid) {
                  a.completed_status = 1;
                }
                return a;
              });
              toaster.message = "success";
            }
          });
      }
      return toaster;
    },
    getReservationStatus(reservation) {
      // If completed
      if (reservation.completed_status == 1) {
        return "completed";
      }

      // Check date status for non-completed reservations
      const now = new Date();
      const endDate = new Date(reservation.requested_for);
      const timeDiff = endDate.getTime() - now.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

      if (daysDiff < 0) {
        // Past the end date - expired
        return "expired";
      } else if (daysDiff <= 5) {
        // Within 5 days - close to ending
        return "close";
      } else {
        // More than 5 days - ongoing
        return "ongoing";
      }
    },
    async reactivateReservation(reservation) {
      try {
        await Api(true).post("/reservation/reactivate", reservation);
        // Update local state - extend by only 1 day from today
        this.reservation_list = this.reservation_list.map((a) => {
          if (a.guid == reservation.guid) {
            const newDate = new Date();
            newDate.setDate(newDate.getDate() + 1); // Add only 1 day
            a.requested_for = newDate.toISOString().split("T")[0];
          }
          return a;
        });
      } catch (error) {
        console.error("Error reactivating reservation:", error);
      }
    },
    async deleteReservation(reservation) {
      try {
        await Api(true).post("/reservation/delete", { guid: reservation.guid });
        // Remove from local state
        this.reservation_list = this.reservation_list.filter((a) => a.guid !== reservation.guid);
      } catch (error) {
        console.error("Error deleting reservation:", error);
      }
    },
    async updateReservationField(reservation, field, value) {
      try {
        await Api(true).post("/reservation/update", {
          guid: reservation.guid,
          field: field,
          value: value,
        });
        // Update local state
        this.reservation_list = this.reservation_list.map((a) => {
          if (a.guid == reservation.guid) {
            a[field] = value;
          }
          return a;
        });
      } catch (error) {
        console.error("Error updating reservation:", error);
      }
    },

    // New action to trigger search (re-fetch data)
    async searchReservations() {
      await this.get_reservations(); // Calls get_reservations which sends date_from/to
    },

    // New action to reset filters and trigger search
    async resetFilters() {
      this.date_from = "";
      this.date_to = "";
      this.search_val = ""; // Also reset the main search bar
      this.status_filter = ""; // Also reset the status filter
      await this.get_reservations(); // Re-fetch with default date (today) and no other filters
    },
  },
});
